import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {RESOURCES as resources} from '../orderResources';
import {postItem} from '../utils/apiOrderFunctions';
import {getGetHeaders} from '../utils/headers';
import ListLoading from './ListLoading';
import OrderEditAddItemContainer from './OrderEditAddItemContainer';
import OrderItemsList from './OrderItemsList';
import FieldDivider from './FieldDivider';
import FieldDescription from './FieldDescription';
import {sortArrayByKey} from '../utils/dataFunctions';

/**
 * A component for retrieving and displaying a Product List by Order ID
 *
 * @param view
 * @param orderData
 * @param orderId
 * @param forPrint
 * @param hasDelete
 * @param hasEditQty
 * @param hasRemove
 * @param props
 * @return {JSX.Element}
 * @constructor
 */
const OrderItemListViewContainer = ({
                                        view,
                                        orderData,
                                        orderId,
                                        forPrint = false,
                                        hasDelete = false,
                                        hasEditQty = true,
                                        hasRemove = false,
                                        ...props
                                    }) => {

    const [itemList, setItemList] = useState({
        products: []
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [updateComplete, setUpdateComplete] = useState(false);
    const [productToUpdate] = useState(null);

    const fetchListData = useCallback(() => {
        // Fetch Products by Order ID
        let id = orderId;
        if (!id) {
            id = (props.id) ? props.id : null;
        }
        if (id && !updateComplete) {
            const fetchItems = () => {
                const params = {
                    active: true,
                    id: id,
                    params: {
                        filter: {},
                        pagination: {page: 1, perPage: 10000},
                        sort: {field: "title", order: "ASC"}
                    }
                };

                const cfg = resources.OrderItem.GET_LIST(params);
                axios({
                    method: 'GET',
                    url: cfg.uri,
                    headers: getGetHeaders(),
                    maxBodyLength: Infinity,
                    maxContentLength: Infinity
                }).then((response) => {
                    if (response.data && response.data.length > 0) {
                        for (let i = 0; i < response.data.length; i++) {
                            const product = response.data[i];
                            // Add Number of boxes if not included
                            if (!product.num_boxes) {
                                product.num_boxes = 1;
                            }
                            // Sort Warehouse Locations for each item
                            product.first_warehouse_location = '';
                            if (product.warehouse_locations && product.warehouse_locations.length > 0) {
                                product.warehouse_locations = sortArrayByKey(product.warehouse_locations, 'location', 'ASC');
                                // Store first Warehouse Location for table sorting
                                product.first_warehouse_location = product.warehouse_locations[0].location;
                            }
                        }
                        // Sort Products by first Warehouse Location
                        response.data = sortArrayByKey(response.data, 'first_warehouse_location', 'ASC');
                        setItemList(itemList => ({...itemList, products: response.data}));
                    } else {
                        setItemList({products: []});
                    }
                    setUpdateComplete(true);
                    setLoading(false);
                }).catch((error) => {
                    console.log('error response for Products: ', error);
                    if (error.response) {
                        // Server response outside 2xx
                    } else if (error.request) {
                        // No response
                    }
                    setLoading(false);
                });
            };

            // If an Order ID becomes available or has changed, or if a refresh is needed, fetch Products
            if ((orderId || props.id) || !updateComplete) {
                setLoading(true);
                fetchItems();
            }
        }
    }, [orderId, props.id, updateComplete]);

    // Effect: Fetch Products by Order ID
    useEffect(
        () => {
            // If an Order ID becomes available or has changed, or if a refresh is needed, fetch Products
            if ((orderId || props.id) || !updateComplete) {
                return fetchListData();
            }


        }, [orderId, props.id, updateComplete, fetchListData]
    );

    const handleAddItem = (e) => {
        const item = (e && e.item) ? e.item : null;
        let id = (props.id) ? parseInt(props.id, 10) : null;
        let qty = (item && item.qty_requested) ? item.qty_requested : 0;
        if (item && item.id) {
            setSaving(true);
            const params = {
                id: item.id,
                order_id: id,
                product_id: item.product_id,
                qty: qty,
                notes: item.notes
            };

            postItem('addItem', params)
                .then(result => {
                    if (result && result.product_id) {
                        // Display message and reset item list
                        setSaveError(null);
                    }
                })
                .then(() => {
                    setSaving(false);
                    setUpdateComplete(false);
                    fetchListData();
                })
                .catch(error => {
                    // Display message
                    setSaving(false);
                    setSaveError('Sorry, there was an error adding this Item.');
                });
        }
    };

    const handleDelete = (id) => {
        const itemId = (id) ? id : null;

        if (itemId) {
            // Post the update for the requested Product
            setSaving(true);
            const params = {
                id: itemId
            };
            postItem('deleteItem', params)
                .then(result => {
                    if (result && result.message && result.message.indexOf('deleted') !== -1) {
                        // Display message and reset item list
                        setSaving(false);
                        setSaveError(null);
                        setUpdateComplete(false);
                        fetchListData();
                    }
                })
                .catch(error => {
                    setSaving(false);
                    setSaveError('Sorry, there was an error deleting this Item.');
                });
        }
    };

    const handleUpdateQty = (e) => {
        const item = (e && e.item) ? e.item : null;
        const newQty = (e && e.new_qty) ? e.new_qty : 0;
        let id = (props.id) ? parseInt(props.id, 10) : null;
        if (item && id) {
            // Post the update for the requested Product
            setSaving(true);
            const params = {
                id: item.id,
                order_id: id,
                product_id: item.product_id,
                qty: item.qty,
                new_qty: newQty,
                notes: item.notes
            };
            postItem('updateItem', params)
                .then(result => {
                    if (result && result.product_id) {
                        // Display message and reset item list
                        setSaving(false);
                        setSaveError(null);
                        setUpdateComplete(false);
                    }
                })
                .catch(error => {
                    console.log('Product Update Error: ', error);
                    // Display message
                    setSaving(false);
                    setSaveError('Sorry, there was an error updating this Item.');
                });
        }
    };

    const handleUpdateNotes = (e) => {
        const item = (e && e.item) ? e.item : null;
        if (item) {
            // Post the update for the requested Product
            setSaving(true);
            const params = {
                id: item.id,
                notes: item.notes
            };
            postItem('updateNotes', params)
                .then(result => {
                    if (result && result.product_id) {
                        // Display message and reset item list
                        setSaving(false);
                        setSaveError(null);
                        setUpdateComplete(false);
                    }
                })
                .catch(error => {
                    console.log('Product Update Error: ', error);
                    // Display message
                    setSaving(false);
                    setSaveError('Sorry, there was an error updating this Item.');
                });
        }
    };

    return (
        <>
            {loading &&
                <ListLoading text="Loading Products..."/>
            }
            <OrderItemsList
                view={view}
                itemList={itemList.products}
                hasDelete={hasDelete}
                hasEditNotes={(view === 'edit')}
                hasEditQty={hasEditQty}
                hasInfo={(!forPrint)}
                hasRemove={hasRemove}
                saving={saving}
                error={saveError}
                onEditQtyFunc={(e) => handleUpdateQty(e)}
                onEditNotesFunc={(e) => handleUpdateNotes(e)}
                onDeleteFunc={(e) => handleDelete(e)}
            />
            {view === 'edit' &&
                <>
                    <FieldDivider type="break" margin="bottom"/>
                    <FieldDescription
                        instructions={true} margin="bottom"
                        text={`Add more Products below if desired. If a Product is already in the list, update its quantity instead.`}
                    />
                    <OrderEditAddItemContainer
                        record={orderData}
                        itemsInOrder={itemList.products}
                        onAddFunc={(e) => handleAddItem(e)}
                        refresh={productToUpdate}
                    />
                    <FieldDivider type="break"/>
                </>
            }
        </>
    );
}

export default OrderItemListViewContainer;
